import {
  DEFAULT_FACET_CONFIG,
  getStoredPreferences,
  savePreferences,
  clearPreferences,
  fetchFacetValues,
} from './algolia-my-size.js';
import { initRefinementListFade } from './algolia-refinement-list-fade.js';

function parseFacetConfig(raw) {
  if (!raw) return DEFAULT_FACET_CONFIG;
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length ? parsed : DEFAULT_FACET_CONFIG;
  } catch (_) {
    return DEFAULT_FACET_CONFIG;
  }
}

function sortFacetValues(values) {
  return values.slice().sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
}

function buildFacetUI(facetConfig, facetData, selectedPrefs) {
  const fragments = document.createDocumentFragment();

  facetConfig.forEach((facet) => {
    const { attribute, label, disjunctive, staticValues } = facet;
    let values = staticValues?.length ? staticValues : Object.keys(facetData?.[attribute] || {});

    if (!values.length) return;

    values = sortFacetValues(values);
    const selected = new Set(selectedPrefs[attribute] || []);

    const wrapper = document.createElement('div');
    wrapper.className = 'ais-facet-dropdown-wrapper page-my-size__facet';
    wrapper.dataset.attribute = attribute;
    wrapper.dataset.disjunctive = disjunctive ? 'true' : 'false';

    const header = document.createElement('div');
    header.className = 'ais-facet--header';
    header.textContent = label || attribute;
    wrapper.appendChild(header);

    const dropdown = document.createElement('div');
    dropdown.className = 'ais-facet-dropdown-container';

    const panel = document.createElement('div');
    panel.className = 'ais-Panel';
    const panelBody = document.createElement('div');
    panelBody.className = 'ais-Panel-body';

    const refinementList = document.createElement('div');
    refinementList.className = 'ais-RefinementList ais-facet';

    const list = document.createElement('ul');
    list.className = 'ais-RefinementList-list';

    values.forEach((value) => {
      const li = document.createElement('li');
      li.className = `ais-RefinementList-item ais-facet--item${selected.has(value) ? ' ais-RefinementList-item--selected' : ''}`;

      const labelEl = document.createElement('label');
      labelEl.className = 'ais-RefinementList-label ais-facet--label';

      const input = document.createElement('input');
      input.type = 'checkbox';
      input.className = 'ais-RefinementList-checkbox ais-facet--checkbox';
      input.name = attribute;
      input.value = value;
      input.checked = selected.has(value);

      labelEl.appendChild(input);
      labelEl.append(document.createTextNode(value));
      li.appendChild(labelEl);
      list.appendChild(li);
    });

    refinementList.appendChild(list);
    panelBody.appendChild(refinementList);
    panel.appendChild(panelBody);
    dropdown.appendChild(panel);
    wrapper.appendChild(dropdown);
    fragments.appendChild(wrapper);
  });

  return fragments;
}

function collectFormPreferences(root) {
  const preferences = {};
  root.querySelectorAll('.page-my-size__facet').forEach((facetEl) => {
    const attribute = facetEl.dataset.attribute;
    const checked = [...facetEl.querySelectorAll('.ais-RefinementList-checkbox:checked')].map(
      (el) => el.value
    );
    if (checked.length) preferences[attribute] = checked;
  });
  return preferences;
}

function showStatus(el, message, isError = false) {
  if (!el) return;
  el.textContent = message;
  el.hidden = false;
  el.classList.toggle('page-my-size__status--error', isError);
}

function interpretSaveResult(result) {
  if (!result || typeof result !== 'object') {
    return { message: 'Could not save preferences. Please try again.', isError: true };
  }

  if (result.ok === true) {
    return {
      message: result.warning || 'Preferences saved in this browser.',
      isError: Boolean(result.warning),
    };
  }

  // Legacy metafield/customer-account save responses (localStorage already updated).
  if (result.savedToMetafield === true) {
    return { message: 'Preferences saved in this browser.', isError: false };
  }
  if (result.savedToMetafield === false && !result.metafieldError) {
    return { message: 'Preferences saved in this browser.', isError: false };
  }
  if (result.metafieldError) {
    return {
      message: result.metafieldErrorMessage
        ? `Preferences saved in this browser. Account sync failed: ${result.metafieldErrorMessage}`
        : 'Preferences saved in this browser.',
      isError: Boolean(result.metafieldErrorMessage),
    };
  }

  return {
    message: result.error || 'Could not save preferences. Please try again.',
    isError: true,
  };
}

async function initMySizePage() {
  const root = document.getElementById('my-size-app');
  if (!root) return;

  const appId = root.dataset.appId;
  const searchKey = root.dataset.searchKey;
  const index = root.dataset.index;
  const facetConfig = parseFacetConfig(root.dataset.facetConfig);
  const storedPrefs = getStoredPreferences();

  const attributes = facetConfig
    .filter((f) => !f.staticValues?.length)
    .map((f) => f.attribute);

  let facetData = null;
  if (appId && searchKey && index && attributes.length) {
    try {
      facetData = await fetchFacetValues({ appId, searchKey, index, attributes });
    } catch (_) {
      facetData = null;
    }
  }

  root.innerHTML = '';

  const form = document.createElement('form');
  form.className = 'page-my-size__form';
  form.noValidate = true;

  const facetsWrap = document.createElement('div');
  facetsWrap.className = 'page-my-size__facets ais-facets';
  facetsWrap.appendChild(buildFacetUI(facetConfig, facetData, storedPrefs));

  if (!facetsWrap.querySelector('.page-my-size__facet')) {
    facetsWrap.innerHTML =
      '<p class="page-my-size__empty">No filter options available. Configure Algolia credentials in section settings or facet fallback JSON.</p>';
  }

  const actions = document.createElement('div');
  actions.className = 'page-my-size__actions contact__form--input';

  const saveBtn = document.createElement('button');
  saveBtn.type = 'submit';
  saveBtn.className = 'button';
  saveBtn.textContent = 'Save my preferences';

  const clearBtn = document.createElement('button');
  clearBtn.type = 'button';
  clearBtn.className = 'ais-ClearRefinements-button button';
  clearBtn.textContent = 'Clear all';

  const status = document.createElement('p');
  status.className = 'page-my-size__status';
  status.hidden = true;
  status.setAttribute('role', 'status');

  actions.append(saveBtn, clearBtn);

  form.append(facetsWrap, actions, status);
  root.appendChild(form);
  initRefinementListFade();

  form.addEventListener('change', (e) => {
    const checkbox = e.target.closest('.ais-RefinementList-checkbox');
    if (!checkbox) return;
    checkbox.closest('.ais-RefinementList-item')?.classList.toggle(
      'ais-RefinementList-item--selected',
      checkbox.checked
    );
  });

  if (!facetData && appId) {
    showStatus(
      status,
      'Showing default filter options. Algolia facet sync unavailable — saved preferences are still outlined on collection pages.',
      false
    );
  }

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    saveBtn.disabled = true;

    try {
      const result = await savePreferences(collectFormPreferences(form));
      const { message, isError } = interpretSaveResult(result);
      showStatus(status, message, isError);
    } catch (_) {
      showStatus(status, 'Could not save preferences. Please try again.', true);
    } finally {
      saveBtn.disabled = false;
    }
  });

  clearBtn.addEventListener('click', () => {
    form.querySelectorAll('.ais-RefinementList-checkbox').forEach((cb) => {
      cb.checked = false;
      cb.closest('.ais-RefinementList-item')?.classList.remove('ais-RefinementList-item--selected');
    });

    const result = clearPreferences();
    showStatus(
      status,
      result.ok ? 'Preferences cleared.' : (result.error || 'Could not clear preferences. Please try again.'),
      !result.ok
    );
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initMySizePage);
} else {
  initMySizePage();
}
